<template>
  <div
    v-if="collections.length"
    ref="sliderRef"
    :class="[$style.catalogueExplrMore, { [$style.catalogueExplrMoreInited]: isSliderInited }]"
  >
    <CatalogueSliderHeader
      :icon="collectionsIcon.url"
      :name="collectionsTitle"
      :show-all-title="showAllTitle"
      :is-loading="(collections.length && !flickingRef ? false : !flickingRef) || !isLoaded"
      :show-all-link="useStoreRoot().localePath('/slot-themes')"
      :no-navigation="slidesPerView >= collections.length"
      @showAll="goToAllCollections"
    />
    <template v-if="collections.length && isLoaded">
      <Flicking
        ref="flickingRef"
        :class="$style.catalogueExplrMoreSlider"
        hide-before-init
        :options="{
          bound: true,
          circular: false,
          align: collections.length > slidesPerView ? 'next' : 'prev',
          inputType: isDesktop ? [] : ['touch', 'mouse'],
          panelsPerView: slidesPerView,
        }"
        @ready="sliderOnReady"
      >
        <div
          v-for="(item, id) in collections"
          :key="item.id"
          :class="$style.catalogueExplrMoreItem"
        >
          <a
            :href="itemPath(item)"
            :class="$style.catalogueExplrMoreItemLink"
            @click.prevent="setSelectedCollections(item, id)"
          >
            <picture v-if="item.exploreMoreCollectionImage?.url">
              <!-- Mobile: <= 768px -->
              <source
                media="(max-width: 768px)"
                :srcset="`${item.exploreMoreCollectionImage.url}?w=350&q=90&fit=cover&format=webp`"
              />
              <!-- Tablet: > 768px -->
              <source
                media="(min-width: 768px)"
                :srcset="`${item.exploreMoreCollectionImage.url}?w=350&q=90&fit=cover&format=webp`"
              />

              <img
                :src="`${item.exploreMoreCollectionImage.url}?w=350&q=90&fit=cover&format=webp`"
                fetchpriority="low"
                loading="lazy"
                :class="$style.gameThumbnailImg"
                :alt="item.exploreMoreCollectionImage.alternativeText || ''"
              />
            </picture>

            <p>{{ item.title }}</p>
          </a>
        </div>
      </Flicking>
    </template>
    <CatalogueSliderExploreMoreSkeleton v-if="!collections.length || !isSliderInited" />
  </div>
</template>

<script lang="ts" setup>
import { Arrow } from '@egjs/flicking-plugins';
import Flicking, { type FlickingEvents } from '@egjs/vue3-flicking';
import { computed, ref } from 'vue';
import CatalogueSliderExploreMoreSkeleton from '~/components/mainPage/catalogueSliderExploreMoreSkeleton.vue';
import CatalogueSliderHeader from '~/components/mainPage/catalogueSliderHeader.vue';
import { useStoreGtm } from '~/stores/abstract/gtm';
import { useStoreRoot } from '~/stores/abstract/root';
import '@egjs/vue3-flicking/dist/flicking.css';
import { useStoreCataloguePage } from '~/stores/strapi/cataloguePage';
import { useStoreSlotTheme } from '~/stores/strapi/slotThemes';
import type { FlickingType } from '~/types/banners';
import type { ISlotThemeData } from '~/types/slots';

defineProps({
  showAllTitle: {
    type: String,
    default: '',
  },
});
defineEmits<{
  (e: 'loaded'): void;
}>();

const storeRoot = useStoreRoot();
const storeCataloguePage = useStoreCataloguePage();

const sliderRef = ref<null | HTMLElement>(null);
const flickingRef = ref<null | FlickingType>(null);
const isSliderInited = ref(false);
const isLoaded = ref(false);

const isDesktop = computed(() => usePlatform().isDesktop);

const slidesPerView = computed(() => {
  if (storeRoot.screen.width > 1920) {
    return 7;
  } else if (storeRoot.screen.width <= 1920 && storeRoot.screen.width > 1632) {
    return 7;
  } else if (storeRoot.screen.width <= 1632 && storeRoot.screen.width > 1239) {
    return 5;
  } else if (storeRoot.screen.width <= 1239 && storeRoot.screen.width > 768) {
    return 4;
  } else if (storeRoot.screen.width <= 768 && storeRoot.screen.width > 580) {
    return 3;
  } else {
    return 2;
  }
});

const collections = computed(() => {
  return useStoreSlotTheme().activeList.filter((el) => el.isExploreMoreCollection);
});

const collectionsTitle = computed(() => storeCataloguePage.exploreMoreTitle);
const collectionsIcon = computed(() => storeCataloguePage.exploreMoreIcon);

watch(
  () => slidesPerView.value < collections.value.length,
  (val, oldVal) => {
    if (!oldVal && val && flickingRef.value) {
      setTimeout(() => {
        flickingRef.value!.activePlugins.forEach((el) => el.update(flickingRef.value!));
      }, 100);
    }
  }
);

const sliderOnReady = (event: FlickingEvents['ready']) => {
  const currentTarget = event.currentTarget;
  currentTarget?.addPlugins(
    new Arrow({
      parentEl: sliderRef.value,
    })
  );
  isSliderInited.value = true;
};

const itemPath = (item: ISlotThemeData) => {
  return useStoreRoot().localePath(
    `${item.isDefaultCategory ? '' : '/slot-themes'}/${item.categoryId}`
  );
};

const setSelectedCollections = async (item: ISlotThemeData, id: number) => {
  useStoreGtm().push({
    event: 'navigation_categories_click',
    event_label: 'explore_more',
    nav_num: id + 1,
  });
  await navigateTo({
    path: itemPath(item),
  });
};

const goToAllCollections = () => {
  return navigateTo({ path: useStoreRoot().localePath('/slot-themes') });
};

onMounted(() => {
  isLoaded.value = true;
});
</script>

<style lang="scss" module src="./catalogueSliderExploreMore.scss"></style>
